import React from 'react';
import { FileText } from 'lucide-react';

const DocumentsContent = ({ patientId }) => {
  // Sample documents data - in a real app, this would come from an API
  const documentsData = {
    medications: [
      { id: 1, name: "Prescription_Jan2024.pdf", date: "Jan 15, 2024" },
      { id: 2, name: "Medicine_List_Dec2023.pdf", date: "Dec 10, 2023" },
      { id: 3, name: "Pharmacy_Receipt.pdf", date: "Nov 22, 2023" },
      { id: 4, name: "Dosage_Instructions.pdf", date: "Nov 18, 2023" },
      { id: 5, name: "Side_Effects_Info.pdf", date: "Oct 30, 2023" },
      { id: 6, name: "Drug_Interactions.pdf", date: "Oct 15, 2023" },
      { id: 7, name: "Refill_Request.pdf", date: "Sep 28, 2023" },
      { id: 8, name: "Allergy_Alert.pdf", date: "Sep 12, 2023" },
      { id: 9, name: "Generic_Options.pdf", date: "Aug 25, 2023" },
      { id: 10, name: "Cost_Analysis.pdf", date: "Aug 10, 2023" },
      { id: 11, name: "Insurance_Coverage.pdf", date: "Jul 20, 2023" },
      { id: 12, name: "Medication_Schedule.pdf", date: "Jul 5, 2023" }
    ],
    pathology: [
      { id: 1, name: "Blood_Test_Results.pdf", date: "Jan 20, 2024" },
      { id: 2, name: "CBC_Report_Dec2023.pdf", date: "Dec 15, 2023" },
      { id: 3, name: "Lipid_Profile.pdf", date: "Nov 28, 2023" },
      { id: 4, name: "Thyroid_Function.pdf", date: "Nov 10, 2023" },
      { id: 5, name: "Liver_Function_Test.pdf", date: "Oct 25, 2023" },
      { id: 6, name: "Kidney_Function.pdf", date: "Oct 8, 2023" },
      { id: 7, name: "Diabetes_Screening.pdf", date: "Sep 20, 2023" },
      { id: 8, name: "Vitamin_Levels.pdf", date: "Sep 5, 2023" },
      { id: 9, name: "Cholesterol_Check.pdf", date: "Aug 18, 2023" },
      { id: 10, name: "Urine_Analysis.pdf", date: "Aug 3, 2023" }
    ],
    imaging: [
      { id: 1, name: "Chest_Xray_Jan2024.pdf", date: "Jan 25, 2024" },
      { id: 2, name: "MRI_Brain_Dec2023.pdf", date: "Dec 20, 2023" },
      { id: 3, name: "CT_Scan_Abdomen.pdf", date: "Nov 15, 2023" },
      { id: 4, name: "Ultrasound_Report.pdf", date: "Oct 30, 2023" },
      { id: 5, name: "Bone_Density_Scan.pdf", date: "Oct 12, 2023" },
      { id: 6, name: "Echocardiogram.pdf", date: "Sep 25, 2023" },
      { id: 7, name: "Mammography_Report.pdf", date: "Sep 8, 2023" },
      { id: 8, name: "PET_Scan_Results.pdf", date: "Aug 22, 2023" }
    ],
    referrals: [
      { id: 1, name: "Cardiology_Referral.pdf", date: "Jan 30, 2024" },
      { id: 2, name: "Specialist_Consultation.pdf", date: "Dec 25, 2023" },
      { id: 3, name: "Physical_Therapy.pdf", date: "Nov 20, 2023" },
      { id: 4, name: "Nutrition_Counseling.pdf", date: "Nov 5, 2023" },
      { id: 5, name: "Mental_Health_Referral.pdf", date: "Oct 18, 2023" },
      { id: 6, name: "Dermatology_Consult.pdf", date: "Oct 2, 2023" },
      { id: 7, name: "Orthopedic_Referral.pdf", date: "Sep 15, 2023" },
      { id: 8, name: "Ophthalmology_Visit.pdf", date: "Aug 30, 2023" }
    ]
  };

  const DocumentCard = ({ document }) => (
    <div className="bg-white border border-gray-200 rounded-md p-2 hover:shadow-md transition-shadow cursor-pointer">
      <div className="flex flex-col items-center space-y-1">
        {/* PDF Icon */}
        <div className="w-8 h-8 bg-red-100 rounded-md flex items-center justify-center">
          <FileText className="h-5 w-5 text-red-600" />
        </div>
        {/* File Name */}
        <div className="text-center w-full">
          <p className="text-xs font-medium text-gray-900 mb-0.5 leading-tight break-words overflow-hidden h-8 flex items-center justify-center" title={document.name}>
            {document.name.length > 12 ? `${document.name.substring(0, 12)}...` : document.name}
          </p>
          <p className="text-xs text-gray-500">{document.date}</p>
        </div>
      </div>
    </div>
  );

  const CategorySection = ({ title, documents, color }) => {
    const colorMap = {
      blue: "bg-blue-50 border-blue-200",
      green: "bg-green-50 border-green-200", 
      purple: "bg-purple-50 border-purple-200",
      orange: "bg-orange-50 border-orange-200"
    };

    const headerColorMap = {
      blue: "text-blue-800",
      green: "text-green-800",
      purple: "text-purple-800", 
      orange: "text-orange-800"
    };

    return (
      <div className={`${colorMap[color]} border rounded-lg p-4 mb-6`}>
        <h3 className={`${headerColorMap[color]} font-semibold mb-4 text-lg`}>{title}</h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 2xl:grid-cols-12 gap-3">
          {documents.map((document) => (
            <DocumentCard key={document.id} document={document} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1">
      {/* Documents Categories */}
      <CategorySection
        title="Medications"
        documents={documentsData.medications}
        color="blue"
      />
      
      <CategorySection
        title="Pathology"
        documents={documentsData.pathology}
        color="green"
      />
      
      <CategorySection
        title="Imaging"
        documents={documentsData.imaging}
        color="purple"
      />
      
      <CategorySection
        title="Referrals"
        documents={documentsData.referrals}
        color="orange"
      />
    </div>
  );
};

export default DocumentsContent;
